<template>
  <net-actionbar
    v-if="!hidden && editor"
    :x="position.x"
    :y="position.y - 15"
    :width="0"
    :height="0"
    class="net-text-toolbar"
    @wheel.prevent="emit('wheel', $event)"
  >
    <li v-if="'toggleBold' in editor.commands" class="net-actionbar__entry">
      <button
        class="net-actionbar__button"
        type="button"
        :class="{ '-active': editor.isActive('bold') }"
        @click="editor.chain().toggleBold().focus().run()"
      >
        <lucide-icon name="bold" />
      </button>
    </li>
    <li v-if="'toggleItalic' in editor.commands" class="net-actionbar__entry">
      <button
        class="net-actionbar__button"
        type="button"
        :class="{ '-active': editor.isActive('italic') }"
        @click="editor.chain().toggleItalic().focus().run()"
      >
        <lucide-icon name="italic" />
      </button>
    </li>
    <li
      v-if="'toggleUnderline' in editor.commands"
      class="net-actionbar__entry"
    >
      <button
        class="net-actionbar__button"
        type="button"
        :class="{ '-active': editor.isActive('underline') }"
        @click="editor.chain().toggleUnderline().focus().run()"
      >
        <lucide-icon name="underline" />
      </button>
    </li>

    <template v-if="hasTextAlign">
      <li class="net-actionbar__entry">
        <tooltip-button
          class="net-actionbar__button"
          :title="$t('labels.alignment')"
          tooltip-class="tooltip-button__tooltip net-text-toolbar__tooltip"
          placement="top"
        >
          <lucide-icon :name="isTextAlign('center') ? 'text-align-center' : isTextAlign('end') ? 'text-align-end' : 'text-align-start'" />
          <template #tooltip>
            <div class="line p-1 gap-1">
              <button
                class="net-actionbar__button"
                type="button"
                :class="{ 'is-active': isTextAlign('start') }"
                @click="setTextAlign('start')"
              >
                <lucide-icon name="text-align-start" />
              </button>
              <button
                class="net-actionbar__button"
                type="button"
                :class="{ 'is-active': isTextAlign('center') }"
                @click="setTextAlign('center')"
              >
                <lucide-icon name="text-align-center" />
              </button>
              <button
                class="net-actionbar__button"
                type="button"
                :class="{ 'is-active': isTextAlign('end') }"
                @click="setTextAlign('end')"
              >
                <lucide-icon name="text-align-end" />
              </button>
            </div>
          </template>
        </tooltip-button>
      </li>
    </template>

    <li
      v-if="hasTextColor"
      class="net-actionbar__entry"
    >
      <input-color-text
        :value="getTextColor()"
        :default-value="contrastTextColor"
        class="net-actionbar__input"
        popout-class="net-text-toolbar__tooltip p-3"
        @update:value="setTextColor($event)"
      />
    </li>

    <template v-if="hasFontSize">
      <li class="net-actionbar__entry">
        <tooltip-button
          class="net-actionbar__button"
          :title="$t('labels.fontSize')"
          tooltip-class="tooltip-button__tooltip net-text-toolbar__tooltip"
          placement="top"
        >
          <lucide-icon name="a-arrow-up-down" />
          <template #tooltip>
            <div class="line p-1 gap-1">
              <button
                class="net-actionbar__button"
                :title="$t('actions.increaseFontSize')"
                type="button"
                @click="increaseFontSize()"
              >
                <lucide-icon name="a-arrow-up" />
              </button>
              <button
                class="net-actionbar__button"
                :title="$t('actions.resetFontSize')"
                type="button"
                @click="resetFontSize()"
              >
                <lucide-icon name="eraser" />
              </button>
              <button
                class="net-actionbar__button"
                :title="$t('actions.decreaseFontSize')"
                type="button"
                @click="decreaseFontSize()"
              >
                <lucide-icon name="a-arrow-down" />
              </button>
            </div>
          </template>
        </tooltip-button>
      </li>
    </template>

    <template v-if="'toggleHeading' in editor.commands">
      <li class="net-actionbar__entry">
        <tooltip-button
          class="net-actionbar__button"
          :title="$t('labels.headings')"
          :class="{ '-active': editor.isActive('heading', { level: 1 }) || editor.isActive('heading', { level: 2 }) || editor.isActive('heading', { level: 3 }) }"
          tooltip-class="tooltip-button__tooltip net-text-toolbar__tooltip"
          placement="top"
        >
          <lucide-icon name="heading" />
          <template #tooltip>
            <div class="line gap-1">
              <button
                class="net-actionbar__button"
                type="button"
                :class="{ '-active': editor.isActive('paragraph') }"
                @click="editor.chain().setParagraph().focus().run()"
              >
                <lucide-icon name="type" />
              </button>
              <button
                class="net-actionbar__button"
                type="button"
                :class="{ '-active': editor.isActive('heading', { level: 1 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
              >
                <lucide-icon name="h-1" />
              </button>
              <button
                class="net-actionbar__button"
                type="button"
                :class="{ '-active': editor.isActive('heading', { level: 2 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
              >
                <lucide-icon name="h-2" />
              </button>
              <button
                class="net-actionbar__button"
                type="button"
                :class="{ '-active': editor.isActive('heading', { level: 3 }) }"
                @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
              >
                <lucide-icon name="h-3" />
              </button>
            </div>
          </template>
        </tooltip-button>
      </li>
    </template>

    <li
      v-if="'toggleBulletList' in editor.commands"
      class="net-actionbar__entry"
    >
      <button
        class="net-actionbar__button"
        type="button"
        :class="{ '-active': editor.isActive('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <lucide-icon name="list" />
      </button>
    </li>
    <li
      v-if="'toggleOrderedList' in editor.commands"
      class="net-actionbar__entry"
    >
      <button
        class="net-actionbar__button"
        type="button"
        :class="{ '-active': editor.isActive('orderedList') }"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <lucide-icon name="list-ordered" />
      </button>
    </li>

    <template v-if="hasLineHeight">
      <li class="net-actionbar__entry">
        <tooltip-button
          class="net-actionbar__button"
          :title="$t('labels.lineHeight')"
          tooltip-class="tooltip-button__tooltip net-text-toolbar__tooltip"
          placement="top"
        >
          <lucide-icon name="list-chevrons-down-up" />
          <template #tooltip>
            <div class="line p-1 gap-1">
              <button
                class="net-actionbar__button"
                type="button"
                :title="$t('actions.increaseLineHeight')"
                @click="increaseLineHeight()"
              >
                <lucide-icon name="plus" />
              </button>
              <button
                class="net-actionbar__button"
                type="button"
                :title="$t('actions.resetLineHeight')"
                @click="resetLineHeight()"
              >
                <lucide-icon name="eraser" />
              </button>
              <button
                class="net-actionbar__button"
                type="button"
                :title="$t('actions.decreaseLineHeight')"
                @click="decreaseLineHeight()"
              >
                <lucide-icon name="minus" />
              </button>
            </div>
          </template>
        </tooltip-button>
      </li>
    </template>

    <li
      v-if="'toggleBlockquote' in editor.commands"
      class="net-actionbar__entry"
    >
      <button
        class="net-actionbar__button"
        type="button"
        :class="{ '-active': editor.isActive('blockquote') }"
        @click="editor.chain().focus().toggleBlockquote().run()"
      >
        <lucide-icon name="quote" />
      </button>
    </li>
    <!-- <button
      class="net-actionbar__button"
      type="button"
      :class="{ '-active': editor.isActive('link') }"
      @click="addLink(editor.isActive('link'))"
    >
      <lucide-icon name="link" />
    </button> -->
    <li
      v-if="'setHorizontalRule' in editor.commands"
      class="net-actionbar__entry"
    >
      <button
        class="net-actionbar__button"
        type="button"
        @click="editor.chain().focus().setHorizontalRule().run()"
      >
        <lucide-icon name="hr" />
      </button>
    </li>
  </net-actionbar>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";

import { useNetTextToolBarContext } from "./netTextToolbarContext";

import LucideIcon from "@core/ui/LucideIcon.vue";
import TooltipButton from "@core/ui/TooltipButton.vue";
import NetActionbar from "./NetActionbar.vue";
import InputColorText from "@core/input/InputColorText.vue";

import type { CanvasSelection } from "./NetCanvasProps";
import type { NetToolKey } from "./NetTools";
import type { SceneTreeItemSize } from "@editor/items/scene/SceneTreeItem";
import type { Uid } from "@/helpers/utility/Id";

const props = defineProps<{
  selection: CanvasSelection;
  itemSizes: { [uid: Uid<"sceneNode" | "flow">]: SceneTreeItemSize };
  tool: NetToolKey;
}>();

const emit = defineEmits<{
  (e: "update:tool", tool: NetToolKey): void;
  (e: "wheel", event: WheelEvent): void;
}>();

const { editor, position, textOptions, updateOption } = useNetTextToolBarContext();

watch(editor, (editor) => {
  console.log("toolbar editor changed", editor);
});

// ==========================
// Text alignment

const hasTextAlign = computed(() => {
  if (!editor.value) return false;
  if ("setTextAlign" in editor.value.commands) return true;
  if ("textAlign" in textOptions.value) return true;
  return false;
});

function setTextAlign(align: CanvasTextAlign) {
  if (!editor.value) return;
  if ("setTextAlign" in editor.value.commands) {
    editor.value.chain().setTextAlign(align).focus().run();
    return;
  }
  if ("textAlign" in textOptions.value) {
    updateOption("textAlign", align);
    return;
  }
}

function isTextAlign(align: CanvasTextAlign) {
  if (!editor.value) return false;
  if (editor.value.isActive({ textAlign: align })) return true;
  if ("textAlign" in textOptions.value) {
    return textOptions.value.textAlign === align;
  }
  return align === "start" || align === "left";
}

// =========================
// Color

const hasTextColor = computed(() => {
  if (!editor.value) return false;
  if ("setColor" in editor.value.commands) return true;
  if ("textColor" in textOptions.value) return true;
  return false;
});

function setTextColor(color: string | null) {
  if (!editor.value) return;
  if ("setColor" in editor.value.commands) {
    if ("textColor" in textOptions.value) {
      if (editor.value.view.state.selection.empty) {
        updateOption("textColor", color);
        return;
      }
    }

    if (color == null) {
      editor.value.chain().unsetColor().focus().run();
    } else {
      editor.value.chain().setColor(color).focus().run();
    }
    return;
  }
  if ("textColor" in textOptions.value) {
    updateOption("textColor", color);
    return;
  }
}

function getTextColor(): string | null {
  if (!editor.value) return null;
  if ("getAttributes" in editor.value) {
    let defaultTextColor = textOptions.value.textColor || null;
    return editor.value.getAttributes("textStyle").color || defaultTextColor;
  }
  if ("textColor" in textOptions.value) {
    return textOptions.value.textColor || null;
  }
  return null;

}

const contrastTextColor = computed(() => {
  return "var(--color-text)";
});

// ==========================
// Font Size

const hasFontSize = computed(() => {
  if (!editor.value) return false;
  if ("setFontSize" in editor.value.commands) return true;
  if ("fontSize" in textOptions.value) return true;
  return false;
});

function increaseFontSize() {
  if (!editor.value) return;
  if ("setFontSize" in editor.value.commands && !editor.value.view.state.selection.empty) {
    const textStyle = editor.value.getAttributes("textStyle");
    console.log("current textStyle:", textStyle);
    const currentSize = parseFloat(textStyle.fontSize || "1");
    console.log("currentSize:", currentSize);
    const newSize = currentSize * 1.2;
    editor.value.chain().setFontSize(newSize + "em").focus().run();
    return;
  }
  if ("fontSize" in textOptions.value) {
    const currentSize = textOptions.value.fontSize || 14;
    updateOption("fontSize", currentSize + 2);
    return;
  }
}

function decreaseFontSize() {
  if (!editor.value) return;
  if ("setFontSize" in editor.value.commands && !editor.value.view.state.selection.empty) {
    const currentSize = parseFloat(
      editor.value.getAttributes("textStyle").fontSize || "1"
    );
    console.log("currentSize:", currentSize);
    const newSize = currentSize / 1.2;
    editor.value.chain().setFontSize(newSize + "em").focus().run();
    return;
  }
  if ("fontSize" in textOptions.value) {
    const currentSize = textOptions.value.fontSize || 14;
    updateOption("fontSize", Math.max(4, currentSize - 2));
    return;
  }
}

function resetFontSize() {
  if (!editor.value) return;
  if ("setFontSize" in editor.value.commands && !editor.value.view.state.selection.empty) {
    editor.value.chain().unsetFontSize().focus().run();
    return;
  }
  if ("fontSize" in textOptions.value) {
    updateOption("fontSize", null);
    return;
  }
}

// ==========================
// Line Height

const LINE_HEIGHT_STEP = 0.05;

const hasLineHeight = computed(() => {
  if (!editor.value) return false;
  if ("setLineHeight" in editor.value.commands) return true;
  if ("lineHeight" in textOptions.value) return true;
  return false;
});

function increaseLineHeight() {
  if (!editor.value) return;
  if ("setLineHeight" in editor.value.commands && !editor.value.view.state.selection.empty) {
    const textStyle = editor.value.getAttributes("textStyle");
    const currentHeight = parseFloat(textStyle.lineHeight || "1.15");
    const newHeight = roundToTwoDecimals(currentHeight + LINE_HEIGHT_STEP);
    editor.value.chain().setLineHeight(newHeight + "").focus().run();
    return;
  }
  if ("lineHeight" in textOptions.value) {
    const currentHeight = textOptions.value.lineHeight || 1.15;
    updateOption("lineHeight", roundToTwoDecimals(currentHeight + LINE_HEIGHT_STEP));
    return;
  }
}

function decreaseLineHeight() {
  if (!editor.value) return;
  if ("setLineHeight" in editor.value.commands && !editor.value.view.state.selection.empty) {
    const textStyle = editor.value.getAttributes("textStyle");
    const currentHeight = parseFloat(textStyle.lineHeight || "1.15");
    const newHeight = roundToTwoDecimals(Math.max(0.4, currentHeight - LINE_HEIGHT_STEP));
    editor.value.chain().setLineHeight(newHeight + "").focus().run();
    return;
  }
  if ("lineHeight" in textOptions.value) {
    const currentHeight = textOptions.value.lineHeight || 1.15;
    updateOption("lineHeight", roundToTwoDecimals(Math.max(0.4, currentHeight - LINE_HEIGHT_STEP)));
    return;
  }
}

function resetLineHeight() {
  if (!editor.value) return;
  if ("setLineHeight" in editor.value.commands && !editor.value.view.state.selection.empty) {
    editor.value.chain().unsetLineHeight().focus().run();
    return;
  }
  if ("lineHeight" in textOptions.value) {
    updateOption("lineHeight", null);
    return;
  }
}

function roundToTwoDecimals(value: number) {
  return Math.round(value * 100) / 100;
}

// ==========================
// Hidden

const hidden = computed(() => {
  return props.tool !== "textedit";
});
</script>

<style lang="scss">
// .net-text-toolbar {
//   // nothing yet
// }
</style>
